/**
 * errorMonitor.js
 *
 * Central error monitoring for the React app.
 * All errors flow through logError() and are batched and sent to the
 * Laravel API via Redux (errorLog.slice.js).
 *
 * Payload shape sent to the backend:
 *   { session_id, log_type, environment, logs: [{ timestamp, severity, ... }] }
 */

import React from 'react';
import HttpStatus from '../helpers/status.enum';

export const ErrorType = Object.freeze({
  API_ERROR:            'API_ERROR',
  NETWORK_ERROR:        'NETWORK_ERROR',
  TIMEOUT_ERROR:        'TIMEOUT_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  VALIDATION_ERROR:     'VALIDATION_ERROR',
  SERVER_ERROR:         'SERVER_ERROR',
  COMPONENT_ERROR:      'COMPONENT_ERROR',
  RUNTIME_ERROR:        'RUNTIME_ERROR',
  PROMISE_REJECTION:    'PROMISE_REJECTION',
  IMAGE_LOAD_ERROR:     'IMAGE_LOAD_ERROR',
  ASSET_LOAD_ERROR:     'ASSET_LOAD_ERROR',
  UNKNOWN_ERROR:        'UNKNOWN_ERROR',
});

export const Severity = Object.freeze({
  CRITICAL: 'critical',
  ERROR:    'error',
  WARNING:  'warning',
  INFO:     'info',
});

// ─── Session ID ───────────────────────────────────────────────────────────────

const getSessionId = () => {
  try {
    let id = localStorage.getItem('hg_session_id');
    if (!id) {
      id = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('hg_session_id', id);
    }
    return id;
  } catch (_) { return 'unknown'; }
};

// ─── Route slug extraction ────────────────────────────────────────────────────

const RESERVED_SEGMENTS     = ['terms', 'privacy'];
const RESERVED_SUB_SEGMENTS = ['guestready'];

const getRouteSlugs = () => {
  try {
    const [listing, booking] = window.location.pathname.split('/').filter(Boolean);
    if (!listing || RESERVED_SEGMENTS.includes(listing.toLowerCase())) {
      return { listing_slug: localStorage.getItem('listingSlug') || null, booking_slug: null };
    }
    return {
      listing_slug: listing,
      booking_slug: booking && !RESERVED_SUB_SEGMENTS.includes(booking.toLowerCase()) ? booking : null,
    };
  } catch (_) {
    return { listing_slug: null, booking_slug: null };
  }
};

// ─── Device and network info (formatted as readable strings for log files) ───

const getDeviceString = () => {
  try {
    const platform = navigator.userAgentData?.platform || navigator.platform || 'unknown';
    const mobile = navigator.userAgentData?.mobile ? 'mobile' : 'desktop';
    const screen = `${window.screen.width}x${window.screen.height}`;
    const viewport = `${window.innerWidth}x${window.innerHeight}`;
    return `${platform} | ${mobile} | screen:${screen} | viewport:${viewport}`;
  } catch (_) { return 'unknown'; }
};

const getNetworkString = () => {
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const online = navigator.onLine ? 'online' : 'offline';
    const type = conn?.effectiveType || '';
    return type ? `${online} | ${type}` : online;
  } catch (_) { return 'unknown'; }
};

// ─── Sensitive data sanitizer ─────────────────────────────────────────────────

const SENSITIVE = [
  'password', 'token', 'access_token', 'refresh_token', 'authorization',
  'api_key', 'apikey', 'cookie', 'secret', 'credit_card', 'cardnumber',
  'card_number', 'cardnum', 'cvv', 'cvc', 'ssn', 'passport', 'otp',
  'security_code', 'pin_code', 'pincode',
];
const SENSITIVE_EXACT = ['auth', 'session', 'card', 'pin'];

const isSensitiveKey = (key) => {
  const k = String(key).toLowerCase();
  return SENSITIVE.some(s => k.includes(s)) || SENSITIVE_EXACT.includes(k);
};

const sanitize = (obj, depth = 0) => {
  if (depth > 5 || obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(i => sanitize(i, depth + 1));
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, isSensitiveKey(k) ? '[REDACTED]' : sanitize(v, depth + 1)])
  );
};

const stripLocalhost = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str.replace(/https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/g, '');
};

const formatApiString = (apiInfo) => {
  if (!apiInfo) return null;
  const clean = sanitize(apiInfo);
  const method = clean.method || '?';
  const url = stripLocalhost(clean.url) || '?';
  const status = clean.status || '?';
  const statusText = clean.status_text ? ` ${clean.status_text}` : '';
  return `${method} ${url} -> ${status}${statusText}`;
};

const formatApiDetails = (apiInfo) => {
  if (!apiInfo) return null;
  const clean = sanitize(apiInfo);
  return {
    url: stripLocalhost(clean.url) || null,
    method: clean.method || null,
    status: clean.status || null,
    status_text: clean.status_text || null,
    error_message: clean.error_message || null,
    request_body: clean.request_body || null,
    response_body: clean.response_body || null,
  };
};

// ─── Batch queue ──────────────────────────────────────────────────────────────
// Errors are queued and flushed via Redux dispatch to the Laravel API.
// Flush happens every 15 seconds, or when 10 errors accumulate, or on page hide.
// A guard flag prevents infinite loops when the log API itself fails.

const BATCH_SIZE  = 10;
const _queue      = [];
let _isFlushing   = false;
let _storeRef     = null;
let _logAction    = null;

export const initErrorLogStore = (store, logClientErrorAction) => {
  _storeRef = store;
  _logAction = logClientErrorAction;
};

const flushQueue = () => {
  if (_queue.length === 0 || _isFlushing || !_storeRef || !_logAction) return;

  _isFlushing = true;
  const batch = _queue.splice(0);

  const payload = {
    session_id:  getSessionId(),
    log_type:    'GUEST',
    environment: process.env.REACT_APP_CUSTOM_NODE_ENV || 'unknown',
    logs:        batch,
  };

  try {
    _storeRef.dispatch(_logAction(payload))
      .catch(() => {})
      .finally(() => { _isFlushing = false; });
  } catch (_) {
    _isFlushing = false;
  }
};

setInterval(flushQueue, 30000);
window.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushQueue();
});

const sendLog = (entry) => {
  try {
    if (_isFlushing) return;
    _queue.push(entry);
    if (_queue.length >= BATCH_SIZE) flushQueue();
  } catch (_) {}
};

// ─── Core log function ────────────────────────────────────────────────────────

export const logError = ({
  message   = 'Unknown error',
  type      = ErrorType.UNKNOWN_ERROR,
  severity  = Severity.ERROR,
  component = null,
  apiInfo   = null,
} = {}) => {
  try {
    const { listing_slug, booking_slug } = getRouteSlugs();
    const timestamp = new Date().toISOString();

    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    sendLog({
      timestamp,
      severity,
      error_type:   type,
      message:      stripLocalhost(String(message)),
      listing_slug: listing_slug || null,
      booking_slug: booking_slug || null,
      page:         isLocalhost ? null : window.location.href,
      route:        window.location.pathname,
      user_agent:   navigator.userAgent || null,
      device:       getDeviceString(),
      network:      getNetworkString(),
      api_summary:  formatApiString(apiInfo),
      api_details:  formatApiDetails(apiInfo),
      component:    component || null,
    });
  } catch (_) {}
};

// ─── API error classifier ─────────────────────────────────────────────────────

export const classifyApiError = (status) => {
  if (!status || status === 0)                                                                    return { type: ErrorType.NETWORK_ERROR,        severity: Severity.ERROR    };
  if (status === HttpStatus.HTTP_UNAUTHORIZED)                                                    return { type: ErrorType.AUTHENTICATION_ERROR,  severity: Severity.WARNING  };
  if (status === HttpStatus.HTTP_FORBIDDEN)                                                       return { type: ErrorType.AUTHENTICATION_ERROR,  severity: Severity.WARNING  };
  if (status === HttpStatus.HTTP_REQUEST_TIMEOUT || status === HttpStatus.HTTP_GATEWAY_TIMEOUT)    return { type: ErrorType.TIMEOUT_ERROR,         severity: Severity.ERROR    };
  if (status === HttpStatus.HTTP_BAD_REQUEST || status === HttpStatus.HTTP_UNPROCESSABLE_ENTITY)  return { type: ErrorType.VALIDATION_ERROR,      severity: Severity.WARNING  };
  if (status >= HttpStatus.HTTP_INTERNAL_SERVER_ERROR)                                            return { type: ErrorType.SERVER_ERROR,          severity: Severity.CRITICAL };
  return                                                                                                 { type: ErrorType.API_ERROR,             severity: Severity.ERROR    };
};

// ─── Global error handlers ────────────────────────────────────────────────────

export const initGlobalErrorHandlers = () => {
  window.onerror = (message, source, lineno, colno) => {
    if (source?.includes('extension://')) return false;
    logError({
      message:  `${String(message)} (${source}:${lineno}:${colno})`,
      type:     ErrorType.RUNTIME_ERROR,
      severity: Severity.ERROR,
    });
    return false;
  };

  window.onunhandledrejection = (event) => {
    const err = event.reason instanceof Error ? event.reason : null;
    logError({
      message:  err?.message || String(event.reason) || 'Unhandled promise rejection',
      type:     ErrorType.PROMISE_REJECTION,
      severity: Severity.ERROR,
    });
  };

  document.addEventListener('error', (event) => {
    const el  = event.target;
    const tag = el?.tagName?.toUpperCase();
    if (!el || el === window || !['IMG', 'SCRIPT', 'LINK'].includes(tag)) return;
    const url = el.src || el.href || 'unknown';
    if (url.startsWith('data:')) return;
    logError({
      message:  `Failed to load ${tag.toLowerCase()}: ${url}`,
      type:     tag === 'IMG' ? ErrorType.IMAGE_LOAD_ERROR : ErrorType.ASSET_LOAD_ERROR,
      severity: Severity.WARNING,
    });
  }, true);

  document.addEventListener('securitypolicyviolation', (event) => {
    logError({
      message:  `Blocked by CSP: ${event.blockedURI} (${event.violatedDirective})`,
      type:     ErrorType.ASSET_LOAD_ERROR,
      severity: Severity.WARNING,
    });
  });
};

// ─── React Error Boundary ─────────────────────────────────────────────────────

class _ErrorBoundaryCore extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) {
    logError({
      message:   error.message || 'Component render error',
      type:      ErrorType.COMPONENT_ERROR,
      severity:  Severity.CRITICAL,
      component: { name: this.props.name || 'Unknown', component_stack: info?.componentStack || null },
    });
  }
  render() { return this.state.hasError ? null : this.props.children; }
}

export const ErrorBoundary = ({ name, children }) => (
  <_ErrorBoundaryCore name={name}>{children}</_ErrorBoundaryCore>
);
